import React, { useState, useEffect, useContext } from "react";
import { useNavigate, useLocation, useOutletContext } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { ChevronLeft, ChevronRight, Calendar, Search, Palmtree, X, Trash2, Plus, CheckSquare, Settings, Check } from "lucide-react";
import { toast } from "react-toastify";
import axios from "axios";
import { UserContext } from "../../contexts/UserContext";
import * as Tooltip from '@radix-ui/react-tooltip';

const API_BASE_URL = `${process.env.REACT_APP_API_URL?.replace(/\/$/, "") || ""}/api`;

// Holiday types
const HOLIDAY_TYPES = {
  H: { label: 'Holiday', color: 'bg-purple-200 border-purple-400', hoverColor: 'bg-purple-300', textColor: 'text-purple-700' },
  S: { label: 'Sickness', color: 'bg-red-200 border-red-400', hoverColor: 'bg-red-300', textColor: 'text-red-700' },
  CC: { label: 'Child Care', color: 'bg-yellow-200 border-yellow-400', hoverColor: 'bg-yellow-300', textColor: 'text-yellow-700' },
  M: { label: 'Maternity', color: 'bg-pink-200 border-pink-400', hoverColor: 'bg-pink-300', textColor: 'text-pink-700' },
  P: { label: 'Paternity', color: 'bg-blue-200 border-blue-400', hoverColor: 'bg-blue-300', textColor: 'text-blue-700' }
};

// Component displaying a single holiday
const HolidayItem = ({ item, onDelete, onEdit, isSelected, onSelect, isMultiSelectMode, onApprove, onReject, currentUser }) => {
  const holidayType = HOLIDAY_TYPES[item.holiday_type] || HOLIDAY_TYPES.H;
  const [showActions, setShowActions] = useState(false);
  
  // Check if current user can approve
  const canApprove = item.status === 'pending' && 
                     currentUser && 
                     (currentUser.is_superuser || currentUser.is_company_admin) &&
                     item.user !== currentUser.id;
  
  // Get status ring color
  const getStatusRing = () => {
    if (isSelected) return 'ring-2 ring-blue-500';
    switch (item.status) {
      case 'pending':
        return 'ring-2 ring-yellow-400';
      case 'rejected':
        return 'ring-2 ring-red-400';
      default:
        return '';
    }
  };
  
  return (
    <div
      onMouseEnter={() => setShowActions(true)}
      onMouseLeave={() => setShowActions(false)}
      onClick={(e) => {
        if (isMultiSelectMode) {
          e.stopPropagation();
          e.preventDefault();
          onSelect(item);
        } else if (!e.defaultPrevented) {
          onEdit(item);
        }
      }}
      className={`p-1 rounded text-[9px] border shadow-sm ${holidayType.color} hover:shadow-md transition-shadow relative overflow-hidden cursor-pointer flex items-center justify-center ${getStatusRing()}`}
      title={item.status === 'pending' ? 'Pending approval' : item.status === 'rejected' ? `Rejected: ${item.rejection_reason || 'No reason provided'}` : ''}
    >
      {isMultiSelectMode && (
        <input
          type="checkbox"
          checked={isSelected}
          onChange={() => onSelect(item)}
          className="h-3 w-3 mr-1"
          onClick={(e) => e.stopPropagation()}
        />
      )}
      
      {/* Show action buttons on hover for admins */}
      {showActions && canApprove && !isMultiSelectMode ? (
        <div className="flex items-center gap-1">
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onApprove(item);
            }}
            className="p-0.5 bg-green-600 text-white rounded hover:bg-green-700 transition-colors"
            title="Approve"
          >
            <Check className="h-3 w-3" />
          </button>
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onReject(item);
            }}
            className="p-0.5 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
            title="Reject"
          >
            <X className="h-3 w-3" />
          </button>
        </div>
      ) : (
        <>
          <span className={`text-[14px] font-bold ${holidayType.textColor}`}>{item.holiday_type}</span>
          {item.status && item.status !== 'approved' && (
            <div className="absolute top-0 right-0">
              {item.status === 'pending' && (
                <div className="w-2 h-2 bg-yellow-500 rounded-full m-0.5" />
              )}
              {item.status === 'rejected' && (
                <div className="w-2 h-2 bg-red-500 rounded-full m-0.5" />
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

// Work/Travel/Training indicators
const WorkIndicator = ({ type }) => {
  const styles = {
    work: { bg: 'bg-yellow-400', text: 'W' },
    travel: { bg: 'bg-green-400', text: 'P' },  // P for Podróż
    training: { bg: 'bg-blue-400', text: 'T' }  // T for Training
  };
  
  const style = styles[type] || styles.work;
  
  return (
    <div className={`inline-flex items-center justify-center w-4 h-4 rounded-full ${style.bg} text-white text-[8px] font-bold`}>
      {style.text}
    </div>
  );
};

// Calendar cell component
const CalendarCell = ({ person, day, dayIndex, holidays, publicHolidays, workPermits, travels, trainings, showWorkTravelTraining, showPublicHolidays, publicHolidayCountries, onDayClick, onDeleteHoliday, onEditHoliday, selectedHolidays, onSelectHoliday, isMultiSelectMode, calendarView, users, onQuickApprove, onQuickReject, currentUser }) => {
  const isToday = day.toDateString() === new Date().toDateString();
  const isWeekend = day.getDay() === 0 || day.getDay() === 6;
  
  const dayHolidays = holidays.filter((item) => {
    const itemDate = new Date(item.date);
    return itemDate.toDateString() === day.toDateString() && item.user === person.id;
  });
  
  const dayPublicHolidays = showPublicHolidays 
    ? publicHolidays.filter((item) => {
        const itemDate = new Date(item.date);
        const countryCode = item.country_code || item.country;
        const matches = itemDate.toDateString() === day.toDateString() && 
               publicHolidayCountries.includes(countryCode);
        if (person.id === users[0]?.id && dayIndex === 0) {
          console.log(`Checking holiday ${item.name} (${countryCode}) for ${day.toDateString()}`);
          console.log(`  Date match: ${itemDate.toDateString() === day.toDateString()}`);
          console.log(`  Country ${countryCode} in list ${publicHolidayCountries}: ${publicHolidayCountries.includes(countryCode)}`);
        }
        if (matches && person.id === users[0]?.id) {
          console.log(`✓ Public holiday found for ${day.toDateString()}: ${item.name} (${item.country})`);
        }
        return matches;
      })
    : [];
  
  // Check for work permits
  const hasWorkPermit = workPermits.some(permit => {
    if (permit.userIdNum !== person.id) return false;
    const start = new Date(permit.start_date);
    const end = new Date(permit.end_date);
    const currentDay = new Date(day);
    // Set time to start of day for accurate comparison
    currentDay.setHours(0, 0, 0, 0);
    start.setHours(0, 0, 0, 0);
    end.setHours(23, 59, 59, 999);
    return currentDay >= start && currentDay <= end;
  });
  
  // Check for travels
  const hasTravel = travels.some(travel => {
    if (travel.userId !== person.id) return false;
    const start = new Date(travel.start_date);
    const end = new Date(travel.end_date);
    const currentDay = new Date(day);
    // Set time to start of day for accurate comparison
    currentDay.setHours(0, 0, 0, 0);
    start.setHours(0, 0, 0, 0);
    end.setHours(23, 59, 59, 999);
    return currentDay >= start && currentDay <= end;
  });
  
  // Check for trainings
  const hasTraining = trainings.some(training => {
    if (training.userId !== person.id) return false;
    const start = new Date(training.start_date);
    const end = new Date(training.end_date);
    const currentDay = new Date(day);
    // Set time to start of day for accurate comparison
    currentDay.setHours(0, 0, 0, 0);
    start.setHours(0, 0, 0, 0);
    end.setHours(23, 59, 59, 999);
    return currentDay >= start && currentDay <= end;
  });

  const hasHoliday = dayHolidays.length > 0;
  const hasPublicHoliday = dayPublicHolidays.length > 0;
  const isWorkDay = !isWeekend && !hasHoliday && !hasPublicHoliday;

  const getCellHeight = () => {
    if (calendarView === "twomonth" || calendarView === "threemonth") {
      return "min-h-[40px]";
    } else if (calendarView === "sixmonth") {
      return "min-h-[30px]";
    }
    return "min-h-[60px]";
  };

  return (
    <div
      className={`border-b border-r p-1 ${getCellHeight()} ${
        hasPublicHoliday ? 'bg-red-100 border-red-300 border-2' :
        isWeekend ? 'bg-gray-100 border-gray-200' : 
        hasHoliday ? 'border-gray-200' : 'bg-white border-gray-200'
      } transition-colors cursor-pointer relative`}
      onClick={() => onDayClick(person, day)}
    >
      {(calendarView === "week" || calendarView === "biweek" || calendarView === "month") && dayHolidays.map((holiday) => (
        <HolidayItem
          key={holiday.id}
          item={holiday}
          onDelete={onDeleteHoliday}
          onEdit={onEditHoliday}
          isSelected={selectedHolidays.some(h => h.id === holiday.id)}
          onSelect={onSelectHoliday}
          isMultiSelectMode={isMultiSelectMode}
          onApprove={onQuickApprove}
          onReject={onQuickReject}
          currentUser={currentUser}
        />
      ))}
      {(calendarView === "twomonth" || calendarView === "threemonth" || calendarView === "sixmonth") && dayHolidays.length > 0 && (
        <div className="flex items-center justify-center h-full">
          <div className="text-[8px] font-medium text-gray-700">{dayHolidays.length}</div>
        </div>
      )}
      {/* Work/Travel/Training indicators */}
      {showWorkTravelTraining && (hasWorkPermit || hasTravel || hasTraining) && (
        <div className="absolute bottom-1 right-1 flex gap-1">
          {hasWorkPermit && <WorkIndicator type="work" />}
          {hasTravel && <WorkIndicator type="travel" />}
          {hasTraining && <WorkIndicator type="training" />}
        </div>
      )}
      {/* Public Holiday indicator */}
      {hasPublicHoliday && (
        <div className="absolute top-0 right-0">
          <div className="bg-red-500 text-white text-[8px] px-1 rounded-bl font-bold">
            {dayPublicHolidays[0].country}
          </div>
        </div>
      )}
    </div>
  );
};

// Add/Edit Holiday Dialog
const HolidayDialog = ({ isOpen, onClose, holidayData, user, date, fetchData, currentUser }) => {
  const [loading, setLoading] = useState(false);
  const [isRangeMode, setIsRangeMode] = useState(false);
  const [showRejectReason, setShowRejectReason] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [holidayDetails, setHolidayDetails] = useState({
    holiday_type: 'H',
    date: date ? date.toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
    end_date: date ? date.toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
    notes: ''
  });
  
  // Check if current user can approve this holiday
  const canApprove = holidayData && 
                     holidayData.status === 'pending' && 
                     currentUser && 
                     (currentUser.is_superuser || currentUser.is_company_admin) &&
                     holidayData.user !== currentUser.id;

  useEffect(() => {
    if (isOpen) {
      setShowRejectReason(false);
      setRejectionReason('');
      if (holidayData) {
        setHolidayDetails({
          holiday_type: holidayData.holiday_type,
          date: holidayData.date,
          end_date: holidayData.date,
          notes: holidayData.notes || ''
        });
        setIsRangeMode(false);
      } else if (date) {
        setHolidayDetails({
          holiday_type: 'H',
          date: date.toISOString().split('T')[0],
          end_date: date.toISOString().split('T')[0],
          notes: ''
        });
        setIsRangeMode(false);
      }
    }
  }, [isOpen, holidayData, date]);

  const handleApproveHoliday = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("access_token");
      
      await axios.post(
        `${API_BASE_URL}/holidays/${holidayData.id}/approve/`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success("Holiday approved successfully");
      onClose();
      fetchData();
    } catch (error) {
      console.error("Error approving holiday:", error);
      toast.error(error.response?.data?.error || "Failed to approve holiday");
    } finally {
      setLoading(false);
    }
  };

  const handleRejectHoliday = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("access_token");
      
      await axios.post(
        `${API_BASE_URL}/holidays/${holidayData.id}/reject/`,
        { rejection_reason: rejectionReason },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success("Holiday rejected");
      onClose();
      fetchData();
    } catch (error) {
      console.error("Error rejecting holiday:", error);
      toast.error(error.response?.data?.error || "Failed to reject holiday");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveHoliday = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("access_token");
      
      if (holidayData) {
        // Update single holiday
        const data = {
          user: user.id,
          holiday_type: holidayDetails.holiday_type,
          date: holidayDetails.date,
          notes: holidayDetails.notes
        };

        const response = await axios.patch(
          `${API_BASE_URL}/holidays/${holidayData.id}/`,
          data,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json"
            }
          }
        );
        console.log('Update response:', response.data);
        toast.success("Holiday updated successfully");
      } else {
        // Create new holiday(s)
        if (isRangeMode) {
          // Calculate all dates in range
          const startDate = new Date(holidayDetails.date);
          const endDate = new Date(holidayDetails.end_date);
          const dates = [];
          
          for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
            // Skip weekends if it's a regular holiday
            if (holidayDetails.holiday_type === 'H' && (d.getDay() === 0 || d.getDay() === 6)) {
              continue;
            }
            dates.push(new Date(d).toISOString().split('T')[0]);
          }

          console.log('Creating holidays for dates:', dates);

          // Create multiple holidays
          const promises = dates.map(date => {
            const data = {
              user: user.id,
              holiday_type: holidayDetails.holiday_type,
              date: date,
              notes: holidayDetails.notes
            };
            
            return axios.post(
              `${API_BASE_URL}/holidays/`,
              data,
              {
                headers: {
                  Authorization: `Bearer ${token}`,
                  "Content-Type": "application/json"
                }
              }
            );
          });

          await Promise.all(promises);
          toast.success(`Successfully created ${dates.length} holidays`);
        } else {
          // Create single holiday
          const data = {
            user: user.id,
            holiday_type: holidayDetails.holiday_type,
            date: holidayDetails.date,
            notes: holidayDetails.notes
          };

          const response = await axios.post(
            `${API_BASE_URL}/holidays/`,
            data,
            {
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json"
              }
            }
          );
          console.log('Create response:', response.data);
          toast.success("Holiday added successfully");
        }
      }
      
      onClose();
      fetchData();
    } catch (error) {
      console.error("Error saving holiday:", error);
      console.error("Error response:", error.response?.data);
      toast.error(`Failed to save holiday: ${error.response?.data?.detail || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            <div className="flex items-center justify-between w-full">
              <div className="flex items-center">
                <Palmtree className="h-5 w-5 mr-2 text-green-600" />
                <span>{holidayData ? 'Edit' : 'Add'} Holiday</span>
              </div>
              {holidayData && holidayData.status && (
                <span className={`text-xs px-2 py-1 rounded-full ml-auto ${
                  holidayData.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                  holidayData.status === 'approved' ? 'bg-green-100 text-green-700' :
                  holidayData.status === 'rejected' ? 'bg-red-100 text-red-700' :
                  'bg-gray-100 text-gray-700'
                }`}>
                  {holidayData.status.charAt(0).toUpperCase() + holidayData.status.slice(1)}
                </span>
              )}
            </div>
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 my-4">
          {/* Show rejection reason if holiday was rejected */}
          {holidayData && holidayData.status === 'rejected' && holidayData.rejection_reason && (
            <div className="bg-red-50 border border-red-200 rounded p-3">
              <p className="text-sm text-red-700">
                <strong>Rejection reason:</strong> {holidayData.rejection_reason}
              </p>
            </div>
          )}
          
          <div>
            <label className="block text-sm text-gray-700 mb-1">Employee</label>
            <input
              type="text"
              className="w-full p-2 text-sm border rounded bg-gray-50"
              value={`${user?.first_name || ''} ${user?.last_name || ''}`}
              disabled
            />
          </div>
          {!holidayData && (
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="range-mode"
                checked={isRangeMode}
                onChange={(e) => setIsRangeMode(e.target.checked)}
                className="h-4 w-4 text-green-600 rounded border-gray-300 focus:ring-green-500"
              />
              <label htmlFor="range-mode" className="text-sm text-gray-700 cursor-pointer">
                Select date range (multiple days)
              </label>
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {isRangeMode ? 'Start Date' : 'Date'}
            </label>
            <input
              type="date"
              className="w-full p-2 text-sm border rounded focus:ring-green-500 focus:border-green-500"
              value={holidayDetails.date}
              onChange={(e) => setHolidayDetails(prev => ({ ...prev, date: e.target.value }))}
              disabled={holidayData && holidayData.status !== 'pending'}
            />
          </div>
          {isRangeMode && !holidayData && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
              <input
                type="date"
                className="w-full p-2 text-sm border rounded focus:ring-green-500 focus:border-green-500"
                value={holidayDetails.end_date}
                min={holidayDetails.date}
                onChange={(e) => setHolidayDetails(prev => ({ ...prev, end_date: e.target.value }))}
              />
              {holidayDetails.date && holidayDetails.end_date && (
                <p className="text-xs text-gray-500 mt-1">
                  {(() => {
                    const start = new Date(holidayDetails.date);
                    const end = new Date(holidayDetails.end_date);
                    let count = 0;
                    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
                      if (holidayDetails.holiday_type === 'H' && (d.getDay() === 0 || d.getDay() === 6)) {
                        continue;
                      }
                      count++;
                    }
                    return `Will create ${count} holiday${count !== 1 ? 's' : ''} (weekends ${holidayDetails.holiday_type === 'H' ? 'excluded' : 'included'})`;
                  })()}
                </p>
              )}
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Holiday Type</label>
            <select
              className="w-full p-2 text-sm border rounded focus:ring-green-500 focus:border-green-500"
              value={holidayDetails.holiday_type}
              onChange={(e) => setHolidayDetails(prev => ({ ...prev, holiday_type: e.target.value }))}
              disabled={holidayData && holidayData.status !== 'pending'}
            >
              {Object.entries(HOLIDAY_TYPES).map(([key, type]) => (
                <option key={key} value={key}>{type.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes (optional)</label>
            <textarea
              className="w-full p-2 text-sm border rounded focus:ring-green-500 focus:border-green-500"
              rows={3}
              value={holidayDetails.notes}
              onChange={(e) => setHolidayDetails(prev => ({ ...prev, notes: e.target.value }))}
              placeholder="Additional notes..."
              disabled={holidayData && holidayData.status !== 'pending'}
            />
          </div>
        </div>
        
        {/* Show rejection reason field when rejecting */}
        {showRejectReason && (
          <div className="space-y-2">
            <label className="block text-sm text-gray-700 mb-1">Rejection Reason (optional)</label>
            <textarea
              className="w-full p-2 text-sm border rounded"
              rows={3}
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              placeholder="Please provide a reason for rejection..."
            />
          </div>
        )}
        
        <DialogFooter className="flex flex-col sm:flex-row gap-2">
          <Button variant="outline" onClick={onClose} disabled={loading} className="w-full sm:w-auto">
            Cancel
          </Button>
          
          {/* Show approval/rejection buttons for pending holidays of other users */}
          {canApprove && (
            <>
              {!showRejectReason ? (
                <div className="flex gap-2 w-full sm:w-auto">
                  <Button
                    onClick={handleApproveHoliday}
                    disabled={loading}
                    className="bg-green-600 hover:bg-green-700 text-white flex-1 sm:flex-initial"
                  >
                    <Check className="h-4 w-4 mr-1" />
                    Approve
                  </Button>
                  <Button
                    onClick={() => setShowRejectReason(true)}
                    disabled={loading}
                    variant="outline"
                    className="text-red-600 hover:text-red-700 hover:bg-red-50 flex-1 sm:flex-initial"
                  >
                    <X className="h-4 w-4 mr-1" />
                    Reject
                  </Button>
                </div>
              ) : (
                <Button
                  onClick={handleRejectHoliday}
                  disabled={loading}
                  className="bg-red-600 hover:bg-red-700 text-white w-full sm:w-auto"
                >
                  Confirm Reject
                </Button>
              )}
            </>
          )}
          
          {/* Show save/update button for own holidays or when not showing approval buttons */}
          {(!canApprove || holidayData?.status !== 'pending') && (
            <Button
              onClick={handleSaveHoliday}
              disabled={loading}
              className="bg-green-600 hover:bg-green-700 text-white w-full sm:w-auto"
            >
              {loading ? "Saving..." : holidayData ? "Update" : "Add"} Holiday
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Main Holiday Calendar Component
const HolidayCalendar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { sidebarCollapsed } = useOutletContext();
  const { user: currentUser } = useContext(UserContext);
  
  const [calendarView, setCalendarView] = useState('month');
  const [currentWeekStart, setCurrentWeekStart] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() - date.getDay() + 1); // Set to Monday
    return date;
  });
  
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [publicHolidays, setPublicHolidays] = useState([]);
  const [workPermits, setWorkPermits] = useState([]);
  const [travels, setTravels] = useState([]);
  const [trainings, setTrainings] = useState([]);
  const [showWorkTravelTraining, setShowWorkTravelTraining] = useState(false);
  const [showPublicHolidays, setShowPublicHolidays] = useState(true);
  const [publicHolidayCountries, setPublicHolidayCountries] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedUserDay, setSelectedUserDay] = useState(null);
  const [editingHoliday, setEditingHoliday] = useState(null);
  const [isMultiSelectMode, setIsMultiSelectMode] = useState(false);
  const [selectedHolidays, setSelectedHolidays] = useState([]);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [availableCountries, setAvailableCountries] = useState([]);
  const [quickRejectHoliday, setQuickRejectHoliday] = useState(null);
  const [quickRejectReason, setQuickRejectReason] = useState('');

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("access_token");
      if (!token) {
        toast.error("No authentication token found");
        return;
      }

      const startDate = new Date(currentWeekStart);
      const endDate = new Date(currentWeekStart);
      const daysToShow = {
        week: 7,
        biweek: 14,
        month: 28,
        twomonth: 56,
        threemonth: 84,
        sixmonth: 168
      }[calendarView] || 28;
      endDate.setDate(startDate.getDate() + daysToShow);

      const config = { headers: { Authorization: `Bearer ${token}` } };
      const holidayConfig = {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          start_date: startDate.toISOString().split("T")[0],
          end_date: endDate.toISOString().split("T")[0],
        },
      };

      console.log('Fetching holidays with params:', holidayConfig.params);

      const [usersResponse, holidaysResponse, publicHolidaysResponse] = await Promise.all([
        axios.get(`${API_BASE_URL}/users/`, config),
        axios.get(`${API_BASE_URL}/holidays/`, holidayConfig),
        axios.get(`${API_BASE_URL}/public-holidays/`, holidayConfig).then(response => {
          console.log('Public holidays raw response:', response);
          return response;
        }).catch(err => {
          console.error('Error fetching public holidays:', err);
          return { data: [] };
        })
      ]);
      
      // Try to fetch work permits, travels and trainings, but don't fail if they don't exist
      let workPermitResponse = { data: [] };
      let travelResponse = { data: [] };
      let trainingResponse = { data: [] };
      
      try {
        workPermitResponse = await axios.get(`${API_BASE_URL}/workpermit/`, holidayConfig);
      } catch (error) {
        console.log('Work permits endpoint not available');
      }
      
      try {
        travelResponse = await axios.get(`${API_BASE_URL}/travel/`, holidayConfig);
      } catch (error) {
        console.log('Travel endpoint not available');
      }
      
      try {
        trainingResponse = await axios.get(`${API_BASE_URL}/user-training-assignments/`, holidayConfig);
      } catch (error) {
        console.log('Trainings endpoint not available');
      }

      console.log('Users response:', usersResponse.data);
      console.log('Holidays response:', holidaysResponse.data);
      console.log('Public holidays response:', publicHolidaysResponse.data);
      console.log('Public holidays count:', publicHolidaysResponse.data?.length || 0);
      if (publicHolidaysResponse.data && publicHolidaysResponse.data.length > 0) {
        console.log('Sample public holidays:', publicHolidaysResponse.data.slice(0, 5));
        const countryCounts = {};
        publicHolidaysResponse.data.forEach(h => {
          countryCounts[h.country] = (countryCounts[h.country] || 0) + 1;
        });
        console.log('Public holidays by country:', countryCounts);
      }

      setUsers(usersResponse.data || []);
      setHolidays(holidaysResponse.data || []);
      setPublicHolidays(publicHolidaysResponse.data || []);
      
      // Fetch available countries and filter only those with holidays
      try {
        const countriesResponse = await axios.get(`${API_BASE_URL}/countries/`, config);
        const countriesData = countriesResponse.data || [];
        // Filter only countries that have holidays
        const countriesWithHolidays = countriesData.filter(country => country.holidays_count > 0);
        console.log('Countries with holidays:', countriesWithHolidays);
        setAvailableCountries(countriesWithHolidays);
      } catch (error) {
        console.log('Countries endpoint not available');
        // If API fails, get unique countries from public holidays
        const uniqueCountries = [...new Set(publicHolidaysResponse.data?.map(h => h.country_code || h.country) || [])];
        const fallbackCountries = uniqueCountries.map(code => ({
          code: code,
          name: code,
          holidays_count: publicHolidaysResponse.data?.filter(h => (h.country_code || h.country) === code).length || 0
        }));
        setAvailableCountries(fallbackCountries);
      }
      
      // Auto-select countries that have holidays if none selected
      if (publicHolidayCountries.length === 0 && publicHolidaysResponse.data?.length > 0) {
        const uniqueCountryCodes = [...new Set(publicHolidaysResponse.data.map(h => h.country_code || h.country))];
        setPublicHolidayCountries(uniqueCountryCodes);
        console.log('Auto-selected countries with holidays:', uniqueCountryCodes);
      }
      
      // Show warning if no public holidays are loaded
      if (!publicHolidaysResponse.data || publicHolidaysResponse.data.length === 0) {
        console.warn('No public holidays found. Run: python manage.py load_public_holidays');
        toast.info('No public holidays loaded. Contact admin to load holiday data.');
      }
      
      // Process work permits (orders)
      const workPermitList = workPermitResponse.data.flatMap(order => {
        // Check if order has team_data
        if (!order.team_data?.users) return [];
        
        return order.team_data.users.map(user => ({
          id: `permit-${order.id}-${user.id}`,
          userId: user.username,
          userIdNum: user.id,
          work_permit_name: order.work_permit_number || order.order_number || "Work Permit",
          start_date: order.planned_date_start,
          end_date: order.planned_date_end,
          type: "workpermit",
        }));
      }).filter(permit => permit.userIdNum && permit.start_date);
      
      // Process travels
      const travelList = travelResponse.data.map(travel => ({
        id: travel.id,
        userId: travel.user_id,  // Use numeric user_id for comparison
        start_date: travel.start_date,
        end_date: travel.end_date,
        location: travel.address || travel.location,
        type: "travel"
      })).filter(travel => travel.userId && travel.start_date);
      
      // Process trainings - matching TravelPanel structure
      const trainingList = trainingResponse.data.map(training => {
        return {
          id: `training-${training.id}`,
          userId: training.user_id,
          trainingName: training.training?.qualification_type?.name || "Training",
          start_date: training.start_date,
          end_date: training.end_date,
          type: "training",
        };
      }).filter(training => training.userId && training.start_date);

      console.log('Work permits:', workPermitList);
      console.log('Travels:', travelList);
      console.log('Trainings:', trainingList);
      console.log('Raw travel response:', travelResponse.data);

      setWorkPermits(workPermitList);
      setTravels(travelList);
      setTrainings(trainingList);
    } catch (error) {
      console.error("Error fetching data:", error);
      console.error("Error response:", error.response?.data);
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [currentWeekStart, calendarView]);

  const navigateWeek = (direction) => {
    const newDate = new Date(currentWeekStart);
    const daysToAdd = direction === "next" ? 
      (calendarView === "week" ? 7 : calendarView === "biweek" ? 14 : calendarView === "month" ? 28 : calendarView === "twomonth" ? 56 : calendarView === "threemonth" ? 84 : 168) : 
      (calendarView === "week" ? -7 : calendarView === "biweek" ? -14 : calendarView === "month" ? -28 : calendarView === "twomonth" ? -56 : calendarView === "threemonth" ? -84 : -168);
    newDate.setDate(newDate.getDate() + daysToAdd);
    setCurrentWeekStart(newDate);
  };

  const getDaysForView = () => {
    const days = [];
    const daysCount = calendarView === "week" ? 7 : calendarView === "biweek" ? 14 : calendarView === "month" ? 28 : calendarView === "twomonth" ? 56 : calendarView === "threemonth" ? 84 : 168;
    for (let i = 0; i < daysCount; i++) {
      const day = new Date(currentWeekStart);
      day.setDate(currentWeekStart.getDate() + i);
      days.push(day);
    }
    return days;
  };

  const handleDayClick = (person, date) => {
    setSelectedUserDay({ person, date });
  };

  const handleDeleteHoliday = async (holiday) => {
    if (window.confirm("Are you sure you want to delete this holiday?")) {
      try {
        const token = localStorage.getItem("access_token");
        await axios.delete(
          `${API_BASE_URL}/holidays/${holiday.id}/`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            }
          }
        );
        toast.success("Holiday deleted successfully");
        fetchData();
      } catch (error) {
        console.error("Error deleting holiday:", error);
        toast.error("Failed to delete holiday");
      }
    }
  };

  const handleEditHoliday = (holiday) => {
    const user = users.find(u => u.id === holiday.user);
    setEditingHoliday({ holiday, user });
  };

  const handleQuickApprove = async (holiday) => {
    try {
      const token = localStorage.getItem("access_token");
      await axios.post(
        `${API_BASE_URL}/holidays/${holiday.id}/approve/`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Holiday approved");
      fetchData();
    } catch (error) {
      console.error("Error approving holiday:", error);
      toast.error(error.response?.data?.error || "Failed to approve holiday");
    }
  };

  const handleQuickReject = (holiday) => {
    setQuickRejectHoliday(holiday);
  };

  const handleQuickRejectConfirm = async () => {
    if (!quickRejectHoliday) return;
    
    try {
      const token = localStorage.getItem("access_token");
      await axios.post(
        `${API_BASE_URL}/holidays/${quickRejectHoliday.id}/reject/`,
        { rejection_reason: quickRejectReason },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Holiday rejected");
      setQuickRejectHoliday(null);
      setQuickRejectReason('');
      fetchData();
    } catch (error) {
      console.error("Error rejecting holiday:", error);
      toast.error(error.response?.data?.error || "Failed to reject holiday");
    }
  };

  const handleSelectHoliday = (holiday) => {
    setSelectedHolidays(prev => {
      const exists = prev.some(h => h.id === holiday.id);
      if (exists) {
        return prev.filter(h => h.id !== holiday.id);
      } else {
        return [...prev, holiday];
      }
    });
  };

  const handleDeleteMultipleHolidays = async () => {
    if (selectedHolidays.length === 0) {
      toast.error("No holidays selected");
      return;
    }

    const confirmDelete = window.confirm(
      `Are you sure you want to delete ${selectedHolidays.length} holiday${selectedHolidays.length > 1 ? 's' : ''}?`
    );

    if (!confirmDelete) {
      return;
    }

    try {
      const token = localStorage.getItem("access_token");
      const promises = selectedHolidays.map(holiday => {
        return axios.delete(
          `${API_BASE_URL}/holidays/${holiday.id}/`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            }
          }
        );
      });

      await Promise.all(promises);
      toast.success(`Successfully deleted ${selectedHolidays.length} holiday${selectedHolidays.length > 1 ? 's' : ''}`);
      setSelectedHolidays([]);
      setIsMultiSelectMode(false);
      fetchData();
    } catch (error) {
      console.error("Error deleting holidays:", error);
      toast.error("Failed to delete some holidays");
    }
  };

  const getActiveUsers = () => {
    let filteredUsers = users;
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filteredUsers = filteredUsers.filter((user) =>
        `${user.first_name} ${user.last_name}`.toLowerCase().includes(searchLower)
      );
    }
    return filteredUsers.sort((a, b) => `${a.last_name} ${a.first_name}`.localeCompare(`${b.last_name} ${b.first_name}`));
  };

  const getUserHolidayStats = (userId) => {
    const currentYear = new Date().getFullYear();
    const userHolidays = holidays.filter(h => 
      h.user === userId && 
      new Date(h.date).getFullYear() === currentYear
    );

    // Group by type and status
    const stats = {
      total: userHolidays.length,
      byType: {},
      byStatus: {
        pending: 0,
        approved: 0,
        rejected: 0
      }
    };

    // Initialize all holiday types
    Object.keys(HOLIDAY_TYPES).forEach(type => {
      stats.byType[type] = {
        total: 0,
        pending: 0,
        approved: 0,
        rejected: 0
      };
    });

    // Count holidays
    userHolidays.forEach(holiday => {
      const type = holiday.holiday_type;
      const status = holiday.status || 'approved'; // Default to approved for old data
      
      if (stats.byType[type]) {
        stats.byType[type].total++;
        stats.byType[type][status]++;
      }
      
      stats.byStatus[status]++;
    });

    return stats;
  };

  const getColumnWidth = () => {
    const userColumnWidth = 180;
    const margin = 18;
    const sidebarWidth = sidebarCollapsed ? 64 : 256;
    const availableWidth = windowWidth - userColumnWidth - margin - sidebarWidth;
    const daysCount = getDaysForView().length;
    const minWidths = {
      week: 70,
      biweek: 70,
      month: 70,
      twomonth: 60,
      threemonth: 55,
      sixmonth: 50,
    };
    const calculatedWidth = availableWidth / daysCount;
    const minWidth = minWidths[calendarView] || 90;
    return `${Math.max(calculatedWidth, minWidth)}px`;
  };

  const getGridMinWidth = () => {
    const columnWidth = parseFloat(getColumnWidth());
    const daysCount = getDaysForView().length;
    const totalWidth = 180 + columnWidth * daysCount;
    return `${totalWidth}px`;
  };

  const formatDateRange = (startDate) => {
    const endDate = new Date(startDate);
    let daysToAdd;
    switch (calendarView) {
      case "biweek":
        daysToAdd = 13;
        break;
      case "month":
        daysToAdd = 27;
        break;
      case "twomonth":
        daysToAdd = 55;
        break;
      case "threemonth":
        daysToAdd = 83;
        break;
      case "sixmonth":
        daysToAdd = 167;
        break;
      default:
        daysToAdd = 6;
    }
    endDate.setDate(startDate.getDate() + daysToAdd);
    return `${startDate.toLocaleDateString("en-US", { day: "numeric", month: "long" })} - ${endDate.toLocaleDateString("en-US", { day: "numeric", month: "long", year: "numeric" })}`;
  };

  const formatDayDisplay = (day, index) => {
    const isToday = day.toDateString() === new Date().toDateString();
    
    if (calendarView === "sixmonth") {
      return (
        <div className="text-xs font-medium text-gray-700 flex flex-col items-center">
          {day.getDate() === 1 && (
            <span className="text-[7px] text-gray-500 font-bold">{day.toLocaleDateString("en-US", { month: "short" })}</span>
          )}
          <span className={`text-[9px] ${isToday ? "font-bold text-green-600" : ""}`}>{day.getDate()}</span>
        </div>
      );
    } else if (calendarView === "twomonth") {
      return (
        <div className="text-xs font-medium text-gray-700 flex flex-col items-center">
          {day.getDate() === 1 && (
            <span className="text-[8px] text-gray-500 font-bold">{day.toLocaleDateString("en-US", { month: "short" })}</span>
          )}
          <span className={`text-[10px] ${isToday ? "font-bold text-green-600" : ""}`}>{day.getDate()}</span>
        </div>
      );
    } else {
      return (
        <div className="text-xs font-medium text-gray-700 flex flex-col items-center">
          <span className="text-[9px] text-gray-500">{day.toLocaleDateString("en-US", { weekday: "short" })}</span>
          <span className={`text-sm ${isToday ? "font-bold text-green-600" : ""}`}>{day.getDate()}</span>
          <span className="text-[9px] text-gray-500">{day.toLocaleDateString("en-US", { month: "short" })}</span>
        </div>
      );
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Tooltip.Provider>
        <div className="min-h-screen bg-white border">
          <div className="bg-gradient-to-r from-green-50 to-teal-50 p-4">
            <div className="flex flex-col space-y-2">
              <div className="flex flex-col md:flex-row md:items-center gap-2">
                <div className="ml-4 items-start text-2xl font-bold text-[#002D43]">
                  Holiday Calendar
                </div>
                <div className="md:flex-1 md:flex md:justify-center">
                  <div className="flex items-center space-x-2">
                    <Button variant="outline" size="sm" onClick={() => navigateWeek("prev")} className="shadow-sm h-8 w-8 p-0">
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <div className="flex items-center space-x-2">
                      <h2 className="text-xl font-bold text-gray-800">{formatDateRange(currentWeekStart)}</h2>
                      <select
                        className="px-2 py-1 pr-8 text-xs border rounded-md shadow-sm bg-white"
                        value={calendarView}
                        onChange={(e) => setCalendarView(e.target.value)}
                      >
                        <option value="week">Week</option>
                        <option value="biweek">2 Weeks</option>
                        <option value="month">Month</option>
                        <option value="twomonth">2 Months</option>
                        <option value="threemonth">3 Months</option>
                        <option value="sixmonth">6 Months</option>
                      </select>
                    </div>
                    <Button variant="outline" size="sm" onClick={() => navigateWeek("next")} className="shadow-sm h-8 w-8 p-0">
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                <div className="flex items-center space-x-4">
                  <div className="w-96 flex items-center bg-white rounded-full border shadow-sm p-1">
                    <Search className="h-5 w-5 text-gray-400 ml-4 mr-2" />
                    <input
                      type="text"
                      placeholder="Search employee..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full p-1 text-sm rounded border-none focus:ring-0 focus:outline-none"
                    />
                  </div>
                  {!isMultiSelectMode ? (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setIsMultiSelectMode(true)}
                      className="shadow-sm flex items-center"
                    >
                      <CheckSquare className="h-4 w-4 mr-1" />
                      Multi-select
                    </Button>
                  ) : (
                    <div className="flex items-center space-x-2">
                      <span className="text-sm text-gray-600">
                        {selectedHolidays.length} selected
                      </span>
                      {selectedHolidays.length > 0 && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleDeleteMultipleHolidays}
                          className="shadow-sm flex items-center text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="h-4 w-4 mr-1" />
                          Delete
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setIsMultiSelectMode(false);
                          setSelectedHolidays([]);
                        }}
                        className="shadow-sm flex items-center"
                      >
                        <X className="h-4 w-4 mr-1" />
                        Cancel
                      </Button>
                    </div>
                  )}
                </div>
              </div>
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-4 text-xs">
                  {showWorkTravelTraining && (
                    <>
                      <div className="flex items-center gap-1">
                        <WorkIndicator type="work" />
                        <span>Work Permit</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <WorkIndicator type="travel" />
                        <span>Travel (Podróż)</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <WorkIndicator type="training" />
                        <span>Training</span>
                      </div>
                    </>
                  )}
                  <div className="flex items-center gap-1">
                    <div className="w-4 h-4 bg-red-100 border-2 border-red-300 rounded relative">
                      <div className="absolute -top-1 -right-1 bg-red-500 text-white text-[6px] px-0.5 rounded">PL</div>
                    </div>
                    <span>Public Holiday (PL/EU)</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-4 h-4 border-2 border-yellow-400 rounded relative">
                      <div className="absolute top-0 right-0 w-2 h-2 bg-yellow-500 rounded-full"></div>
                    </div>
                    <span>Pending</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-4 h-4 border-2 border-red-400 rounded relative">
                      <div className="absolute top-0 right-0 w-2 h-2 bg-red-500 rounded-full"></div>
                    </div>
                    <span>Rejected</span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <label className="flex items-center text-xs cursor-pointer">
                    <input
                      type="checkbox"
                      checked={showWorkTravelTraining}
                      onChange={(e) => setShowWorkTravelTraining(e.target.checked)}
                      className="mr-1 h-3 w-3 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                    />
                    <span className="text-gray-700">Show Work/Travel/Training</span>
                  </label>
                  <label className="flex items-center text-xs cursor-pointer">
                    <input
                      type="checkbox"
                      checked={showPublicHolidays}
                      onChange={(e) => setShowPublicHolidays(e.target.checked)}
                      className="mr-1 h-3 w-3 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                    />
                    <span className="text-gray-700">Show Public Holidays</span>
                  </label>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate('/holiday/public')}
                    className="text-xs flex items-center gap-1"
                  >
                    <Settings className="h-3 w-3" />
                    Manage Holidays
                  </Button>
                </div>
              </div>
              {showPublicHolidays && (
                <div className="flex items-center gap-2 ml-4 flex-wrap">
                  <span className="text-xs text-gray-600">Countries:</span>
                  {availableCountries.length > 0 ? (
                    availableCountries.slice(0, 10).map(country => (
                      <label key={country.code} className="flex items-center text-xs cursor-pointer">
                        <input
                          type="checkbox"
                          checked={publicHolidayCountries.includes(country.code)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setPublicHolidayCountries([...publicHolidayCountries, country.code]);
                            } else {
                              setPublicHolidayCountries(publicHolidayCountries.filter(c => c !== country.code));
                            }
                          }}
                          className="mr-1 h-3 w-3 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                        />
                        <span className="text-gray-700">{country.code}</span>
                      </label>
                    ))
                  ) : (
                    <span className="text-xs text-gray-500">No countries with holidays defined</span>
                  )}
                  {availableCountries.length > 10 && (
                    <span className="text-xs text-gray-500">+{availableCountries.length - 10} more</span>
                  )}
                </div>
              )}
            </div>
          </div>
          <div className="p-0">
            {loading ? (
              <div className="py-8 flex flex-col items-center justify-center">
                <p className="text-gray-600 text-sm">Loading calendar...</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <div className="max-h-[calc(100vh-120px)] overflow-y-auto">
                  <div
                    className="inline-grid"
                    style={{
                      gridTemplateColumns: `180px repeat(${getDaysForView().length}, ${getColumnWidth()})`,
                      minWidth: getGridMinWidth(),
                    }}
                  >
                    <div className="sticky top-0 left-0 z-30 bg-gray-100 font-semibold border-b">
                      <div className="w-full h-full p-2 ml-2 flex items-center text-xs font-medium text-gray-700">
                        Employees ({getActiveUsers().length})
                      </div>
                    </div>
                    {getDaysForView().map((day, index) => {
                      const isToday = day.toDateString() === new Date().toDateString();
                      const isWeekend = day.getDay() === 0 || day.getDay() === 6;
                      const dayPublicHolidays = showPublicHolidays 
                        ? publicHolidays.filter((item) => {
                            const itemDate = new Date(item.date);
                            const countryCode = item.country_code || item.country;
                            return itemDate.toDateString() === day.toDateString() && 
                                   publicHolidayCountries.includes(countryCode);
                          })
                        : [];
                      const hasPublicHoliday = dayPublicHolidays.length > 0;
                      
                      return (
                        <div
                          key={index}
                          className={`sticky top-0 z-20 p-1 border-b ${
                            hasPublicHoliday ? "bg-red-100" :
                            isWeekend ? "bg-red-50" : 
                            isToday ? "bg-green-50 border border-green-400" : 
                            "bg-gray-100"
                          }`}
                          title={hasPublicHoliday ? dayPublicHolidays.map(h => h.name).join(', ') : ''}
                        >
                          {formatDayDisplay(day, index)}
                          {hasPublicHoliday && calendarView !== "sixmonth" && (
                            <div className="text-[7px] text-red-600 font-medium text-center truncate px-1">
                              {dayPublicHolidays.map(h => h.country_code || h.country).filter((v, i, a) => a.indexOf(v) === i).join('/')}
                            </div>
                          )}
                        </div>
                      );
                    })}
                    {getActiveUsers().map((person) => {
                      const stats = getUserHolidayStats(person.id);
                      const currentYear = new Date().getFullYear();
                      
                      return (
                        <React.Fragment key={person.id}>
                          <Tooltip.Root>
                            <Tooltip.Trigger asChild>
                              <div className="sticky left-0 z-10 bg-white p-2 hover:bg-gray-50 border-b border-r cursor-pointer">
                                <div className="w-full h-full cursor-pointer flex items-center justify-between">
                                  <div className="ml-2 font-medium text-xs text-gray-900 truncate">
                                    {person.first_name} {person.last_name}
                                  </div>
                                  {stats.total > 0 && (
                                    <div className="mr-2 text-[10px] text-gray-500">
                                      {stats.total}d
                                    </div>
                                  )}
                                </div>
                              </div>
                            </Tooltip.Trigger>
                            <Tooltip.Portal>
                              <Tooltip.Content 
                                className="bg-gray-900 text-white px-3 py-2 rounded-md shadow-lg z-50"
                                sideOffset={5}
                              >
                                <div className="space-y-2">
                                  <div className="font-semibold border-b border-gray-700 pb-1">
                                    {person.first_name} {person.last_name} - {currentYear}
                                  </div>
                                  <div className="text-xs space-y-1">
                                    <div className="font-medium">Holiday Summary:</div>
                                    {Object.entries(stats.byType).map(([type, typeStats]) => {
                                      if (typeStats.total === 0) return null;
                                      const holidayType = HOLIDAY_TYPES[type];
                                      return (
                                        <div key={type} className="flex justify-between items-center gap-4">
                                          <span>{holidayType.label}:</span>
                                          <div className="flex gap-2 text-[10px]">
                                            <span className="font-medium">{typeStats.total} days</span>
                                            {typeStats.pending > 0 && (
                                              <span className="text-yellow-300">({typeStats.pending} pending)</span>
                                            )}
                                            {typeStats.rejected > 0 && (
                                              <span className="text-red-300">({typeStats.rejected} rejected)</span>
                                            )}
                                          </div>
                                        </div>
                                      );
                                    })}
                                    <div className="border-t border-gray-700 pt-1 mt-1 font-medium">
                                      Total: {stats.total} days
                                      {stats.byStatus.pending > 0 && (
                                        <span className="text-yellow-300 text-[10px] ml-1">
                                          ({stats.byStatus.pending} pending approval)
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                </div>
                                <Tooltip.Arrow className="fill-gray-900" />
                              </Tooltip.Content>
                            </Tooltip.Portal>
                          </Tooltip.Root>
                        {getDaysForView().map((day, dayIndex) => (
                          <CalendarCell
                            key={dayIndex}
                            person={person}
                            day={day}
                            dayIndex={dayIndex}
                            holidays={holidays}
                            publicHolidays={publicHolidays}
                            workPermits={workPermits}
                            travels={travels}
                            trainings={trainings}
                            showWorkTravelTraining={showWorkTravelTraining}
                            showPublicHolidays={showPublicHolidays}
                            publicHolidayCountries={publicHolidayCountries}
                            onDayClick={handleDayClick}
                            onDeleteHoliday={handleDeleteHoliday}
                            onEditHoliday={handleEditHoliday}
                            selectedHolidays={selectedHolidays}
                            onSelectHoliday={handleSelectHoliday}
                            isMultiSelectMode={isMultiSelectMode}
                            onQuickApprove={handleQuickApprove}
                            onQuickReject={handleQuickReject}
                            currentUser={currentUser}
                            calendarView={calendarView}
                            users={users}
                          />
                          ))}
                        </React.Fragment>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
            {selectedUserDay && (
              <HolidayDialog
                isOpen={!!selectedUserDay}
                onClose={() => setSelectedUserDay(null)}
                holidayData={null}
                user={selectedUserDay.person}
                date={selectedUserDay.date}
                fetchData={fetchData}
                currentUser={currentUser}
              />
            )}
            {editingHoliday && (
              <HolidayDialog
                isOpen={!!editingHoliday}
                onClose={() => setEditingHoliday(null)}
                holidayData={editingHoliday.holiday}
                user={editingHoliday.user}
                currentUser={currentUser}
                date={null}
                fetchData={fetchData}
              />
            )}
            
            {/* Quick Reject Dialog */}
            <Dialog open={!!quickRejectHoliday} onOpenChange={() => {
              setQuickRejectHoliday(null);
              setQuickRejectReason('');
            }}>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Reject Holiday Request</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <p className="text-sm text-gray-600 mb-2">
                      Rejecting holiday request for {quickRejectHoliday && new Date(quickRejectHoliday.date).toLocaleDateString()}
                    </p>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Rejection Reason (optional)
                    </label>
                    <textarea
                      value={quickRejectReason}
                      onChange={(e) => setQuickRejectReason(e.target.value)}
                      className="w-full px-3 py-2 border rounded-md"
                      rows="3"
                      placeholder="Please provide a reason for rejection..."
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => {
                    setQuickRejectHoliday(null);
                    setQuickRejectReason('');
                  }}>
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleQuickRejectConfirm}
                    className="bg-red-600 hover:bg-red-700 text-white"
                  >
                    Reject Holiday
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
            
          </div>
        </div>
      </Tooltip.Provider>
    </div>
  );
};

export default HolidayCalendar;